import React, { useState, useEffect } from "react";
import { db, storage } from "../../firebase";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from "firebase/storage";
import "./admin.css";

export default function CarouselManager() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("contests");

  const [contestImages, setContestImages] = useState([]);
  const [recognitionImages, setRecognitionImages] = useState([]);

  const [uploadProgress, setUploadProgress] = useState({});
  const [error, setError] = useState(null);

  const CAROUSELS_DOC = "carouselImages";

  // Load carousel images from Firestore
  useEffect(() => {
    loadCarouselImages();
  }, []);

  const loadCarouselImages = async () => {
    try {
      setLoading(true);
      const snap = await getDoc(doc(db, "config", CAROUSELS_DOC));
      if (snap.exists()) {
        const data = snap.data();
        setContestImages(data.contests || []);
        setRecognitionImages(data.recognition || []);
      }
    } catch (e) {
      console.error("Error loading carousel images:", e);
      setError("Failed to load carousel images");
    } finally {
      setLoading(false);
    }
  };

  const saveCarouselImages = async () => {
    try {
      setSaving(true);
      await setDoc(
        doc(db, "config", CAROUSELS_DOC),
        {
          contests: contestImages,
          recognition: recognitionImages,
          updatedAt: new Date(),
        },
        { merge: true }
      );
      alert("Carousel images saved successfully!");
    } catch (e) {
      console.error("Error saving carousel images:", e);
      setError("Failed to save carousel images");
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (e, category) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    for (const file of files) {
      await uploadImageFile(file, category);
    }
  };

  const uploadImageFile = async (file, category) => {
    try {
      const timestamp = Date.now();
      const filename = `${timestamp}_${file.name}`;
      const storagePath = `carousel/${category}/${filename}`;
      const storageRef = ref(storage, storagePath);

      const uploadTask = uploadBytesResumable(storageRef, file);

      uploadTask.on(
        "state_changed",
        (snapshot) => {
          const progress =
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress((prev) => ({
            ...prev,
            [filename]: progress,
          }));
        },
        (error) => {
          console.error("Upload error:", error);
          setError(`Upload failed for ${file.name}`);
          setUploadProgress((prev) => {
            const copy = { ...prev };
            delete copy[filename];
            return copy;
          });
        },
        async () => {
          try {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            const imageData = {
              id: timestamp,
              url: downloadURL,
              name: file.name,
              uploadedAt: new Date().toISOString(),
            };

            if (category === "contests") {
              setContestImages((prev) => [...prev, imageData]);
            } else if (category === "recognition") {
              setRecognitionImages((prev) => [...prev, imageData]);
            }

            setUploadProgress((prev) => {
              const copy = { ...prev };
              delete copy[filename];
              return copy;
            });
          } catch (e) {
            console.error("Error getting download URL:", e);
            setError("Failed to process uploaded image");
          }
        }
      );
    } catch (e) {
      console.error("Error uploading file:", e);
      setError("Upload failed");
    }
  };

  const deleteImage = async (id, category) => {
    if (!window.confirm("Delete this image?")) return;

    try {
      const images = category === "contests" ? contestImages : recognitionImages;
      const image = images.find((img) => img.id === id);

      if (image) {
        // Extract storage path from URL
        const urlParts = image.url.split("%2F");
        if (urlParts.length > 0) {
          try {
            // Delete from Firebase Storage
            const fileRef = ref(storage, `carousel/${category}/${image.name}`);
            await deleteObject(fileRef);
          } catch (e) {
            console.warn("Could not delete from storage:", e);
          }
        }
      }

      // Remove from state
      if (category === "contests") {
        setContestImages((prev) => prev.filter((img) => img.id !== id));
      } else if (category === "recognition") {
        setRecognitionImages((prev) => prev.filter((img) => img.id !== id));
      }
    } catch (e) {
      console.error("Error deleting image:", e);
      setError("Failed to delete image");
    }
  };

  const reorderImages = (category, fromIndex, toIndex) => {
    const images = category === "contests" ? contestImages : recognitionImages;
    const newImages = [...images];
    const [moved] = newImages.splice(fromIndex, 1);
    newImages.splice(toIndex, 0, moved);

    if (category === "contests") {
      setContestImages(newImages);
    } else {
      setRecognitionImages(newImages);
    }
  };

  const ImagesList = ({ images, category }) => (
    <div className="carousel-images-list">
      {images.length === 0 ? (
        <div className="empty-state">
          <p>No images uploaded yet</p>
          <p style={{ fontSize: "12px", opacity: 0.7 }}>
            Upload images to get started
          </p>
        </div>
      ) : (
        <div className="images-grid">
          {images.map((image, idx) => (
            <div key={image.id} className="image-card">
              <div className="image-preview">
                <img src={image.url} alt={image.name} />
              </div>
              <div className="image-info">
                <p className="image-name">{image.name}</p>
                <p className="image-order">Position: {idx + 1}</p>
                <div className="image-actions">
                  {idx > 0 && (
                    <button
                      className="action-btn move-up"
                      onClick={() => reorderImages(category, idx, idx - 1)}
                      title="Move up"
                    >
                      ↑
                    </button>
                  )}
                  {idx < images.length - 1 && (
                    <button
                      className="action-btn move-down"
                      onClick={() => reorderImages(category, idx, idx + 1)}
                      title="Move down"
                    >
                      ↓
                    </button>
                  )}
                  <button
                    className="action-btn delete"
                    onClick={() => deleteImage(image.id, category)}
                    title="Delete"
                  >
                    ✕
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="carousel-manager">
        <p>Loading carousel images...</p>
      </div>
    );
  }

  return (
    <div className="carousel-manager">
      <style>{`
        .carousel-manager {
          display: flex;
          flex-direction: column;
          gap: 20px;
          padding: 16px 0;
        }

        .carousel-tabs {
          display: flex;
          gap: 8px;
          border-bottom: 2px solid rgba(255, 255, 255, 0.1);
          margin-bottom: 16px;
        }

        .tab-button {
          padding: 12px 20px;
          background: transparent;
          border: none;
          color: rgba(255, 255, 255, 0.6);
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          border-bottom: 3px solid transparent;
          margin-bottom: -2px;
        }

        .tab-button.active {
          color: #fff;
          border-bottom-color: #ffd700;
        }

        .tab-button:hover {
          color: rgba(255, 255, 255, 0.8);
        }

        .carousel-section {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .upload-area {
          border: 2px dashed rgba(255, 255, 255, 0.2);
          border-radius: 12px;
          padding: 32px;
          text-align: center;
          cursor: pointer;
          transition: all 0.2s;
          background: rgba(255, 255, 255, 0.02);
        }

        .upload-area:hover {
          border-color: rgba(255, 215, 0, 0.4);
          background: rgba(255, 215, 0, 0.05);
        }

        .upload-area input {
          display: none;
        }

        .upload-area-content {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 12px;
        }

        .upload-icon {
          font-size: 32px;
          opacity: 0.7;
        }

        .upload-text {
          color: rgba(255, 255, 255, 0.8);
          font-size: 14px;
          font-weight: 500;
        }

        .upload-subtext {
          color: rgba(255, 255, 255, 0.5);
          font-size: 12px;
        }

        .carousel-images-list {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .empty-state {
          text-align: center;
          padding: 40px 20px;
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 12px;
          opacity: 0.6;
        }

        .empty-state p {
          margin: 8px 0;
        }

        .images-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 16px;
        }

        .image-card {
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 12px;
          overflow: hidden;
          transition: all 0.2s;
        }

        .image-card:hover {
          border-color: rgba(255, 215, 0, 0.3);
          background: rgba(255, 215, 0, 0.05);
        }

        .image-preview {
          width: 100%;
          aspect-ratio: 16/9;
          overflow: hidden;
          background: rgba(0, 0, 0, 0.3);
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .image-preview img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .image-info {
          padding: 12px;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .image-name {
          font-size: 13px;
          font-weight: 600;
          margin: 0;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .image-order {
          font-size: 11px;
          color: rgba(255, 255, 255, 0.5);
          margin: 0;
        }

        .image-actions {
          display: flex;
          gap: 6px;
          justify-content: flex-end;
        }

        .action-btn {
          padding: 6px 10px;
          background: rgba(255, 255, 255, 0.08);
          border: 1px solid rgba(255, 255, 255, 0.15);
          border-radius: 6px;
          color: rgba(255, 255, 255, 0.8);
          font-size: 12px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          flex: 1;
          text-align: center;
        }

        .action-btn:hover {
          background: rgba(255, 255, 255, 0.12);
          border-color: rgba(255, 255, 255, 0.25);
        }

        .action-btn.delete:hover {
          background: rgba(255, 76, 76, 0.2);
          border-color: rgba(255, 76, 76, 0.4);
          color: #ff4c4c;
        }

        .progress-bar {
          width: 100%;
          height: 4px;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 2px;
          overflow: hidden;
          margin-top: 4px;
        }

        .progress-fill {
          height: 100%;
          background: linear-gradient(90deg, #ffd700, #ffed4e);
          border-radius: 2px;
          transition: width 0.2s;
        }

        .controls {
          display: flex;
          gap: 12px;
          justify-content: flex-end;
          margin-top: 20px;
          padding-top: 20px;
          border-top: 1px solid rgba(255, 255, 255, 0.1);
        }

        .btn-primary {
          padding: 10px 24px;
          background: rgba(255, 215, 0, 0.2);
          border: 1px solid rgba(255, 215, 0, 0.4);
          color: #ffd700;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-primary:hover:not(:disabled) {
          background: rgba(255, 215, 0, 0.3);
          border-color: rgba(255, 215, 0, 0.6);
        }

        .btn-primary:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .error-message {
          padding: 12px 16px;
          background: rgba(255, 76, 76, 0.1);
          border: 1px solid rgba(255, 76, 76, 0.3);
          border-radius: 8px;
          color: #ff6b6b;
          font-size: 13px;
        }

        .info-text {
          font-size: 12px;
          color: rgba(255, 255, 255, 0.5);
          margin: 8px 0 0 0;
        }
      `}</style>

      <div>
        <h2 style={{ margin: "0 0 20px 0" }}>Manage Carousel Images</h2>

        {error && (
          <div className="error-message">
            {error}
            <button
              onClick={() => setError(null)}
              style={{
                marginLeft: "12px",
                background: "none",
                border: "none",
                color: "#ff6b6b",
                cursor: "pointer",
                fontSize: "16px",
              }}
            >
              ✕
            </button>
          </div>
        )}
      </div>

      <div className="carousel-tabs">
        <button
          className={`tab-button ${activeTab === "contests" ? "active" : ""}`}
          onClick={() => setActiveTab("contests")}
        >
          Events & Contests
        </button>
        <button
          className={`tab-button ${activeTab === "recognition" ? "active" : ""}`}
          onClick={() => setActiveTab("recognition")}
        >
          Recognition
        </button>
      </div>

      <div className="carousel-section">
        {activeTab === "contests" && (
          <>
            <div
              className="upload-area"
              onClick={(e) => e.currentTarget.querySelector("input").click()}
            >
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={(e) => handleImageUpload(e, "contests")}
              />
              <div className="upload-area-content">
                <div className="upload-icon">📸</div>
                <div className="upload-text">Click to upload images</div>
                <div className="upload-subtext">
                  PNG, JPG, GIF up to 10MB each
                </div>
              </div>
            </div>

            <ImagesList images={contestImages} category="contests" />
          </>
        )}

        {activeTab === "recognition" && (
          <>
            <div
              className="upload-area"
              onClick={(e) => e.currentTarget.querySelector("input").click()}
            >
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={(e) => handleImageUpload(e, "recognition")}
              />
              <div className="upload-area-content">
                <div className="upload-icon">📸</div>
                <div className="upload-text">Click to upload images</div>
                <div className="upload-subtext">
                  PNG, JPG, GIF up to 10MB each
                </div>
              </div>
            </div>

            <ImagesList images={recognitionImages} category="recognition" />
          </>
        )}
      </div>

      <div className="controls">
        <button
          className="btn-primary"
          onClick={loadCarouselImages}
          style={{ background: "rgba(255, 255, 255, 0.1)" }}
        >
          Refresh
        </button>
        <button
          className="btn-primary"
          onClick={saveCarouselImages}
          disabled={saving}
        >
          {saving ? "Saving..." : "Save All Changes"}
        </button>
      </div>

      <p className="info-text">
        💡 Tip: Drag images in the list to reorder them. Changes are reflected
        on the home page automatically.
      </p>
    </div>
  );
}
